import { useMemo } from "react";

import { findCitations, pipe } from "@utils/ai-context.util";
import { RelatedEntity } from "@app-types/related-items.type";
import { CITATION_REGEX } from "@config/constants";

export function useTransformAIContext(
  content: string = "",
  sourcesFromDB: { [key: string]: RelatedEntity } = {}
) {
  const text = useMemo(() => {
    const text = pipe(
      content,
      (text) => findCitations(text, "(($1))"),
      (text) => text.replace(/\[|\]/g, "") // removes all "[" and "]" symbols
    );
    return [text].filter(Boolean).join("\n");
  }, [content]);

  // 1) citationsSources "13" -> { ..., source_index: 13 }
  const citationsSources = useMemo(() => {
    const result: Record<number, RelatedEntity> = {};

    Object.values(sourcesFromDB).forEach((s) => {
      const idx =
        typeof s.source_index === "number" ? s.source_index : parseInt(String(s.source_index), 10);

      if (!Number.isNaN(idx)) {
        result[idx] = s;
      }
    });

    return result;
  }, [sourcesFromDB]);

  // 2) orderedCitations: "13" -> 1, "22" -> 2, ...
  const orderedCitations = useMemo(() => {
    const matches = [...text.matchAll(CITATION_REGEX)];
    const citations: Record<string, number> = {};
    let index = 1;

    matches.forEach((match) => {
      const numbers = match[1].match(/\d+/g) || [];

      numbers.forEach((numStr) => {
        if (!citations[numStr]) {
          citations[numStr] = index++;
        }
      });
    });

    return citations;
  }, [text]);

  // array of lines for showing bullets
  const lines = useMemo(() => {
    return text
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
  }, [text]);

  return { text, lines, citationsSources, orderedCitations };
}
